import { Link, useParams } from "react-router-dom";
import WorkCard from "@/components/feature/WorkCard";
import usePageMeta from "@/hooks/usePageMeta";
import { worksList } from "@/mocks/works";

export default function WorkDetailPage() {
  const { id } = useParams();
  const work = worksList.find((w) => String(w.id) === id);

  usePageMeta({
    title: work ? `${work.title} - 大岛优香资讯` : "作品详情 - 大岛优香资讯",
    description: work
      ? `${work.title}：${work.synopsis}（${work.type}，${work.year}年，大岛优香${work.role}）`
      : "大岛优香作品详情，了解作品信息与角色介绍。",
    keywords: work
      ? `大岛优香,${work.title},${work.type},${work.genre}`
      : "大岛优香,作品详情",
  });

  if (!work) {
    return (
      <div className="w-full px-4 md:px-6 py-20 text-center">
        <span className="w-14 h-14 rounded-full bg-background-200 text-foreground-500 flex items-center justify-center mx-auto mb-4">
          <i className="ri-film-line text-2xl" />
        </span>
        <p className="font-heading font-bold text-foreground-800 mb-4">未找到这部作品</p>
        <Link
          to="/works"
          className="inline-block px-5 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          返回作品一览
        </Link>
      </div>
    );
  }

  const related = worksList
    .filter((w) => w.id !== work.id && (w.type === work.type || w.genre === work.genre))
    .slice(0, 3);

  const meta = [
    { label: "作品类型", value: work.type },
    { label: "题材", value: work.genre },
    { label: "导演", value: work.director },
    { label: "上映年份", value: `${work.year} 年` },
    { label: "片长 / 集数", value: work.duration },
    { label: "状态", value: work.status },
  ];

  return (
    <div className="w-full px-4 md:px-6 py-8 md:py-10">
      <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap">
        <Link to="/" className="hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
        <Link to="/works" className="hover:text-primary-600">
          作品一览
        </Link>
        <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
        <span className="text-foreground-800">{work.title}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
        <div className="lg:col-span-1">
          <div className="w-full aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
            <img
              src={work.cover}
              alt={work.title}
              title={`${work.title} - 大岛优香影视作品`}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="bg-background-100 border border-background-200 rounded-lg p-5 mt-5">
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground-600">作品评分</span>
              <div className="flex items-center gap-1">
                <i className="ri-star-fill text-primary-500 w-5 h-5 flex items-center justify-center" />
                <span className="font-heading text-xl font-bold text-primary-600">
                  {work.rating}
                </span>
                <span className="text-xs text-foreground-400">/ 10</span>
              </div>
            </div>
            <p className="text-xs text-foreground-400 mt-2">（评分为本站演示示例，非真实评价数据）</p>
          </div>
        </div>

        <div className="lg:col-span-2">
          <span className="inline-block px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-medium mb-3">
            {work.type} · {work.status}
          </span>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
            {work.title}
          </h1>

          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mb-6 pb-6 border-b border-background-200">
            {meta.map((m) => (
              <div key={m.label} className="flex items-center gap-2 text-sm">
                <dt className="text-foreground-500 shrink-0 w-20">{m.label}</dt>
                <dd className="text-foreground-900">{m.value}</dd>
              </div>
            ))}
            <div className="flex items-start gap-2 text-sm sm:col-span-2">
              <dt className="text-foreground-500 shrink-0 w-20">主要演员</dt>
              <dd className="text-foreground-900">{work.cast}</dd>
            </div>
          </dl>

          <section className="mb-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3">剧情简介</h2>
            <p className="text-[15px] text-foreground-800 leading-8">{work.synopsis}</p>
          </section>

          <section className="mb-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3">
              大岛优香 · 角色介绍
            </h2>
            <div className="bg-accent-50 border border-accent-200 rounded-lg p-5">
              <p className="font-heading font-bold text-accent-900 mb-2">{work.role}</p>
              <p className="text-[15px] text-foreground-800 leading-8">{work.roleIntro}</p>
            </div>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3">剧照</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {work.stills.map((still, i) => (
                <div key={i} className="w-full h-52 rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={still}
                    alt={`${work.title}剧照 ${i + 1}`}
                    title={`${work.title} - 大岛优香剧照`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="font-heading text-xl font-bold text-foreground-950 mb-5">相关推荐</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {related.map((w) => (
              <WorkCard key={w.id} work={w} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}